import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { EventModal } from '@/features/calendar/components/EventModal';
import { TaskDetailModal } from '@/features/daily-tasks/components/TaskDetailModal';
import { dailyTasksService, type DailyTask } from '@/features/daily-tasks/services/daily-tasks.service';
import { useNotionTaskModal } from '@/contexts/NotionTaskModalContext';
import { useQueryClient } from '@tanstack/react-query';

type DailyTaskModalContext = 'my-dashboard' | 'admin-board' | 'other';

interface GlobalTaskModalContextType {
  openDailyTaskModal: (task: DailyTask, context?: DailyTaskModalContext) => void;
  openCalendarEventModal: (eventId: string) => void;
  openNotionTaskModal: (task: DailyTask) => void;
}

const MODAL_QUERY_KEYS = ['modal', 'taskId', 'eventId', 'context', 'pageId'] as const;
const MODAL_QUERY_EVENT = 'asseto-modal-query-change';

const GlobalTaskModalContext = createContext<GlobalTaskModalContextType | undefined>(undefined);

const normalizeContext = (value: string | null): DailyTaskModalContext =>
  value === 'my-dashboard' || value === 'other' ? value : 'admin-board';

const updateModalQuery = (
  updates: Partial<Record<(typeof MODAL_QUERY_KEYS)[number], string | null>>,
  replace = false,
) => {
  const params = new URLSearchParams(window.location.search);

  Object.entries(updates).forEach(([key, value]) => {
    if (!value) {
      params.delete(key);
      return;
    }
    params.set(key, value);
  });

  const query = params.toString();
  const nextUrl = query ? `${window.location.pathname}?${query}${window.location.hash}` : `${window.location.pathname}${window.location.hash}`;
  const method = replace ? 'replaceState' : 'pushState';
  window.history[method](window.history.state, '', nextUrl);
  window.dispatchEvent(new Event(MODAL_QUERY_EVENT));
};

const clearModalQuery = () => {
  const updates: Partial<Record<(typeof MODAL_QUERY_KEYS)[number], string | null>> = {};
  MODAL_QUERY_KEYS.forEach((key) => {
    updates[key] = null;
  });
  // replace: fechar não pode empilhar histórico (senão Voltar reabre o modal)
  updateModalQuery(updates, true);
};

export function GlobalTaskModalProvider({ children }: { children: ReactNode }) {
  const notionModal = useNotionTaskModal();
  const queryClient = useQueryClient();
  const [dailyTask, setDailyTask] = useState<DailyTask | null>(null);
  const [dailyTaskContext, setDailyTaskContext] = useState<DailyTaskModalContext>('admin-board');
  const [dailyTaskOpen, setDailyTaskOpen] = useState(false);
  const [eventId, setEventId] = useState<string | null>(null);
  const [eventOpen, setEventOpen] = useState(false);
  const [locationTick, setLocationTick] = useState(0);
  const pendingUrlTaskIdRef = useRef<string | null>(null);

  useEffect(() => {
    const handleLocationChange = () => setLocationTick((value) => value + 1);
    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener(MODAL_QUERY_EVENT, handleLocationChange);
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener(MODAL_QUERY_EVENT, handleLocationChange);
    };
  }, []);

  const closeDailyTaskModal = useCallback(() => {
    setDailyTaskOpen(false);
    setDailyTask(null);
    clearModalQuery();
    // Garante refresh das colunas após edição no modal, mesmo ao fechar imediatamente.
    queryClient.invalidateQueries({ queryKey: ['daily-tasks'] });
    queryClient.invalidateQueries({ queryKey: ['team-calendar-tasks'] });
  }, [queryClient]);

  const closeCalendarEventModal = useCallback(() => {
    setEventOpen(false);
    setEventId(null);
    clearModalQuery();
  }, []);

  const openDailyTaskModal = useCallback(
    (task: DailyTask, context: DailyTaskModalContext = 'admin-board') => {
      setEventOpen(false);
      setEventId(null);
      setDailyTask(task);
      setDailyTaskContext(context);
      setDailyTaskOpen(true);
      updateModalQuery({
        modal: 'daily-task',
        taskId: task.id,
        eventId: null,
        pageId: null,
        context,
      });
    },
    [],
  );

  const openCalendarEventModal = useCallback((calendarEventId: string) => {
    setEventId(calendarEventId);
    setEventOpen(true);
    updateModalQuery({
      modal: 'calendar-event',
      eventId: calendarEventId,
      taskId: null,
      pageId: null,
      context: null,
    });
  }, []);

  const openNotionTaskModal = useCallback(
    (task: DailyTask) => {
      if (!task.notionRefId || !task.notionLink) {
        return;
      }
      notionModal.openModal(task.notionRefId, task.notionLink);
      updateModalQuery({
        modal: 'notion-task',
        taskId: task.id,
        pageId: task.notionRefId,
        eventId: null,
        context: null,
      });
    },
    [notionModal],
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const modal = params.get('modal');
    const urlTaskId = params.get('taskId');
    const urlEventId = params.get('eventId');
    const urlContext = normalizeContext(params.get('context'));
    const urlPageId = params.get('pageId');

    if (modal === 'daily-task' && urlTaskId) {
      if (dailyTaskOpen && dailyTask?.id === urlTaskId) {
        return;
      }
      if (pendingUrlTaskIdRef.current === urlTaskId) {
        return;
      }
      pendingUrlTaskIdRef.current = urlTaskId;
      void dailyTasksService
        .getTask(urlTaskId)
        .then((task) => {
          setEventOpen(false);
          setEventId(null);
          setDailyTask(task);
          setDailyTaskContext(urlContext);
          setDailyTaskOpen(true);
        })
        .catch((error) => {
          console.error('Erro ao abrir tarefa por URL:', error);
          clearModalQuery();
        })
        .finally(() => {
          pendingUrlTaskIdRef.current = null;
        });
      return;
    }

    if (modal === 'calendar-event' && urlEventId) {
      if (eventOpen && eventId === urlEventId) {
        return;
      }
      setEventId(urlEventId);
      setEventOpen(true);
      return;
    }

    if (modal === 'notion-task') {
      if (notionModal.isOpen) {
        return;
      }
      if (urlPageId) {
        notionModal.openModal(urlPageId);
        return;
      }
      if (urlTaskId) {
        if (pendingUrlTaskIdRef.current === urlTaskId) {
          return;
        }
        pendingUrlTaskIdRef.current = urlTaskId;
        void dailyTasksService
          .getTask(urlTaskId)
          .then((task) => {
            if (task.notionRefId) {
              notionModal.openModal(task.notionRefId, task.notionLink ?? undefined);
            } else {
              clearModalQuery();
            }
          })
          .catch((error) => {
            console.error('Erro ao abrir tarefa do Notion por URL:', error);
            clearModalQuery();
          })
          .finally(() => {
            pendingUrlTaskIdRef.current = null;
          });
      }
      return;
    }

    if (!modal) {
      if (dailyTaskOpen) {
        setDailyTaskOpen(false);
        setDailyTask(null);
      }
      if (eventOpen) {
        setEventOpen(false);
        setEventId(null);
      }
      if (notionModal.isOpen) {
        notionModal.closeModal();
      }
    }
  }, [locationTick, dailyTask, dailyTaskOpen, eventId, eventOpen, notionModal]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const modal = params.get('modal');
    if (modal === 'notion-task' && !notionModal.isOpen) {
      clearModalQuery();
    }
  }, [notionModal.isOpen]);

  const contextValue = useMemo(
    () => ({
      openDailyTaskModal,
      openCalendarEventModal,
      openNotionTaskModal,
    }),
    [openDailyTaskModal, openCalendarEventModal, openNotionTaskModal],
  );

  return (
    <GlobalTaskModalContext.Provider value={contextValue}>
      {children}

      {dailyTask && (
        <TaskDetailModal
          open={dailyTaskOpen}
          onClose={closeDailyTaskModal}
          task={dailyTask}
          creationContext={dailyTaskContext}
        />
      )}

      {eventId && (
        <EventModal
          eventId={eventId}
          open={eventOpen}
          onOpenChange={(open) => {
            if (!open) {
              closeCalendarEventModal();
            } else {
              setEventOpen(true);
            }
          }}
        />
      )}
    </GlobalTaskModalContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook de contexto fica junto do Provider
export function useGlobalTaskModal() {
  const context = useContext(GlobalTaskModalContext);
  if (!context) {
    throw new Error('useGlobalTaskModal must be used within a GlobalTaskModalProvider');
  }
  return context;
}
